import { StrictMode } from 'react'
import * as Sentry from '@sentry/react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import dayjs from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import minMax from 'dayjs/plugin/minMax'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import ReactDOM from 'react-dom/client'

import { ThemeProvider } from './app/providers'
import { routeTree } from './app/route-tree.generated'
import { ErrorPage } from './pages/error'
import { AXIOS_INSTANCE, resolveApiBaseUrl } from './shared/api'

import '@fontsource/noto-sans/400.css'
import '@fontsource/noto-sans/500.css'
import '@fontsource/noto-sans/700.css'
import '@/shared/styles/global.scss'

import '@holbonkit/theme/system'
import './sentry'

/**
 * Устанавливаем глобальные плагины для dayjs
 */
dayjs.extend(customParseFormat)
dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(minMax)
dayjs.extend(relativeTime)
dayjs.extend(calendar)
dayjs.locale('ru')

const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultStructuralSharing: true,
    defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
    interface IRegister {
        router: typeof router
    }
}

// QueryClient для React Query с обработкой ошибок в Sentry
const queryClient = new QueryClient({
    mutationCache: new MutationCache({
        onError: (error) => {
            // При ошибке мутации отправляем её в Sentry
            Sentry.captureException(error, {
                tags: {
                    errorType: 'react-query-mutation',
                },
            })
        },
    }),
    queryCache: new QueryCache({
        onError: (error) => {
            // При ошибке запроса отправляем её в Sentry
            Sentry.captureException(error, {
                tags: {
                    errorType: 'react-query',
                },
            })
        },
    }),
})

async function deferRender(): Promise<ReactDOM.Root> {
    if (__GLOBAL__.VITE_API_URL) {
        // ВРЕМЕННО: ?apiurl=indelsb переключает payment API на indels-b (тесты drivee-id на проде)
        AXIOS_INSTANCE.defaults.baseURL = resolveApiBaseUrl()
        AXIOS_INSTANCE.defaults.timeout = 10_000
        AXIOS_INSTANCE.defaults.withCredentials = true
    }

    const rootElement = document.querySelector('#root')

    if (!rootElement) {
        throw new Error('Root element not found')
    }

    return ReactDOM.createRoot(rootElement)
}

deferRender().then((root) => {
    root.render(
        <StrictMode>
            <ThemeProvider>
                <Sentry.ErrorBoundary
                    showDialog
                    fallback={({ error, resetError }) => (
                        <ErrorPage
                            description='Ошибка в Sentry'
                            error={error instanceof Error ? error.message : String(error)}
                            resetError={resetError}
                            title='Ошибка в Sentry'
                        />
                    )}
                >
                    <QueryClientProvider client={queryClient}>
                        <RouterProvider router={router} />
                        <ReactQueryDevtools initialIsOpen={false} />
                    </QueryClientProvider>
                </Sentry.ErrorBoundary>
            </ThemeProvider>
        </StrictMode>,
    )
})
